/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export interface DashboardInsightsExpandableProps {
	children: ReactNode;
	title: string;
	defaultExpanded?: boolean;
	showExpandAtEnd?: boolean;
	titleFontWeight?: 'bold' | 'medium' | 'regular' | 'semibold';
	onExpandChanged?: (expanded: boolean) => void;
	testId?: string;
}

const contentStyles = cssMap({
	base: {
		marginTop: token('space.050'),
	},
	expandAtStart: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.400'),
	},
	expandAtEnd: {
		marginRight: token('space.050'),
		paddingRight: token('space.400'),
	},
});

/**
 * A reusable expandable section component that can be used to show/hide content.
 */
export const DashboardInsightsExpandable = ({
	children,
	title,
	defaultExpanded = false,
	showExpandAtEnd = false,
	titleFontWeight = 'medium',
	onExpandChanged,
	testId = 'expandable-section',
}: DashboardInsightsExpandableProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	useEffect(() => {
		setIsExpanded(defaultExpanded);
	}, [defaultExpanded]);

	const setIsExpandedValue = useCallback(
		(value: boolean) => {
			setIsExpanded(value);
			onExpandChanged?.(value);
		},
		[setIsExpanded, onExpandChanged],
	);

	return (
		<Box data-expanded={isExpanded} testId={testId}>
			<Inline
				spread={showExpandAtEnd ? 'space-between' : undefined}
				alignBlock="center"
				space="space.050"
			>
				{!showExpandAtEnd && (
					<IconButton
						appearance="subtle"
						icon={(iconProps) =>
							isExpanded ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
						onClick={() => setIsExpandedValue(!isExpanded)}
					/>
				)}
				<Text weight={titleFontWeight}>{title}</Text>
				{showExpandAtEnd && (
					<IconButton
						appearance="subtle"
						icon={(iconProps) =>
							isExpanded ? (
								<ChevronDownIcon {...iconProps} size="small" />
							) : (
								<ChevronRightIcon {...iconProps} size="small" />
							)
						}
						label={isExpanded ? formatMessage(messages.collapse) : formatMessage(messages.expand)}
						onClick={() => setIsExpandedValue(!isExpanded)}
					/>
				)}
			</Inline>
			<ExitingPersistence>
				{isExpanded && (
					<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
						{(motionProps) => (
							<div
								{...motionProps}
								css={[
									contentStyles.base,
									showExpandAtEnd && contentStyles.expandAtEnd,
									!showExpandAtEnd && contentStyles.expandAtStart,
								]}
							>
								{children}
							</div>
						)}
					</FadeIn>
				)}
			</ExitingPersistence>
		</Box>
	);
};
